/**
 * Small pieces the homepage blocks share: the section heading in direction B's style (the owner's pick of 2026-10-24,
 * docs/design/pages/home.md), a separator dot drawn in CSS (the copy stays ASCII), placeholders that hold their size
 * while data loads, and the 44px chip link.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SectionAction, type SectionActionLink } from "@/components/ds";
import { cn } from "@/lib/utils";

/**
 * A homepage section heading: an H2 in Inter Black, an optional one-line summary and an optional action link on the
 * right. No numbered mono eyebrow and no hairline (direction B); the sections are set apart by space. `label` is a
 * small plain label above the title, for a section that must say what it is (the store block's "Featured store").
 */
export function HomeHeading({
  id,
  title,
  summary,
  action,
  label,
  className,
}: {
  id: string;
  title: ReactNode;
  summary?: ReactNode;
  action?: SectionActionLink | ReactNode;
  label?: string;
  className?: string;
}) {
  const link = isActionLink(action) ? action : null;
  return (
    <div className={cn("mb-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-2 sm:mb-8", className)}>
      <div className="min-w-0 max-w-3xl">
        {label && <p className="type-small mb-2 font-semibold text-muted-foreground">{label}</p>}
        <h2 id={id} className="scroll-mt-24 text-3xl font-black tracking-tight text-foreground">
          {title}
        </h2>
        {summary && <p className="type-body mt-2 text-muted-foreground">{summary}</p>}
      </div>
      {link ? <SectionAction {...link} /> : (action as ReactNode)}
    </div>
  );
}

function isActionLink(a: unknown): a is SectionActionLink {
  return typeof a === "object" && a !== null && "href" in a && "label" in a;
}

/**
 * The vertical rhythm between homepage sections: 64px on a phone, 96px from 1024px. Tighter than the site's sectionY
 * (80 and 128), because direction B separates sections by space alone, without the hairline the other templates draw.
 */
export const homeSectionY = "py-8 lg:py-12";

/** A dot between two items on one line. Screen readers hear a comma. */
export function DotSep({ className }: { className?: string }) {
  return (
    <>
      <span className="sr-only">, </span>
      <span aria-hidden className={cn("mx-2 inline-block h-[3px] w-[3px] rounded-full bg-current align-middle opacity-60", className)} />
    </>
  );
}

/** A grey block of a fixed size for content that has not loaded yet. */
export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden className={cn("block animate-pulse rounded bg-foreground/[0.07]", className)} />;
}

/**
 * A number that arrives late. While loading, the placeholder text is laid out invisibly in the same font under a
 * grey block, so the real number lands without moving anything.
 */
export function LateNumber({ loading, value, placeholder = "000" }: { loading: boolean; value: ReactNode; placeholder?: string }) {
  if (!loading) return <>{value}</>;
  return (
    <span className="relative inline-block">
      <span className="invisible">{placeholder}</span>
      <span aria-hidden className="absolute inset-0 animate-pulse rounded bg-foreground/[0.07]" />
    </span>
  );
}

/** A link styled as a chip: 44px tall, sentence case, white with a ring, as the hero's popular chips. */
export function ChipLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link
      to={to}
      className={cn(
        "type-label inline-flex min-h-11 items-center rounded-full bg-card px-4 text-foreground shadow-sm ring-1 ring-border transition-shadow duration-150 hover:ring-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      {children}
    </Link>
  );
}

/** A small label and a row of chip links on one wrapping line. */
export function LinkRow({ label, links, className }: { label: string; links: { to: string; label: string }[]; className?: string }) {
  return (
    // The label sits in the list as its first item so it wraps with the chips; the list is named after it for
    // screen readers, which skip the visible copy.
    <ul aria-label={label} className={cn("flex flex-wrap items-center gap-2", className)}>
      <li aria-hidden="true" className="type-small mr-2 font-semibold text-foreground">
        {label}
      </li>
      {links.map((l) => (
        <li key={l.to}>
          <ChipLink to={l.to}>{l.label}</ChipLink>
        </li>
      ))}
    </ul>
  );
}

/** A sub-heading inside a section ("Vendor codes", "Free tools") with an optional link on the right. */
export function SubHeading({ id, children, action }: { id?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex min-h-6 items-center justify-between gap-4">
      <h3 id={id} className="type-body font-bold text-foreground">
        {children}
      </h3>
      {action}
    </div>
  );
}
